import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, Package, ExternalLink, Download, Star, X, Eye, EyeOff } from "lucide-react";

const CATEGORIES = ["Recovery", "Comfort", "Self Care", "Entertainment", "Nutrition"];

// Minimal CSV parser: handles quoted fields with commas and escaped quotes ("")
function parseCSV(text) {
  const rows = [];
  let cur = [];
  let field = "";
  let inQuotes = false;
  let i = 0;
  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQuotes = false; i++; continue;
      }
      field += c; i++; continue;
    }
    if (c === '"') { inQuotes = true; i++; continue; }
    if (c === ",") { cur.push(field); field = ""; i++; continue; }
    if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      cur.push(field); rows.push(cur); cur = []; field = ""; i++; continue;
    }
    field += c; i++;
  }
  if (field !== "" || cur.length) { cur.push(field); rows.push(cur); }
  return rows.filter((r) => r.length && r.some((v) => (v || "").trim() !== ""));
}

function parseBool(v) {
  if (typeof v === "boolean") return v;
  const s = (v || "").toString().trim().toLowerCase();
  return s === "true" || s === "1" || s === "yes" || s === "y";
}

function parseTags(v) {
  if (!v) return [];
  // Support pipe or semicolon separators to avoid CSV comma collision
  return v.toString().split(/[|;]/).map((t) => t.trim()).filter(Boolean);
}

const CSV_TEMPLATE =
  "product_name,base_url,image_url,category,description,price,tags,is_featured\n" +
  '"Memory Foam Wedge Pillow","https://www.amazon.com/dp/B0BTY7G4HX","https://example.com/pillow.jpg","Recovery","Elevates upper body for comfortable sleep.",59.99,"sleep|post-op|bed-rest",true\n' +
  '"Silk Sleep Mask","https://www.amazon.com/dp/B07RSJTPZW","","Comfort","Block light for deeper rest.",14.00,"sleep|gift-under-20",false\n';

function downloadTemplate() {
  const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "curated-products-template.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function ProductsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | item
  const [filterCategory, setFilterCategory] = useState("All");
  const [onlyFeatured, setOnlyFeatured] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [importing, setImporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/curated");
      setItems(data);
    } catch {
      toast.error("Couldn't load products.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this product? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/curated/${id}`);
      setItems((prev) => prev.filter((it) => it.id !== id));
      toast.success("Product removed.");
    } catch {
      toast.error("Couldn't delete.");
    }
  };

  const toggleFeatured = async (item) => {
    try {
      const { data } = await api.put(`/admin/curated/${item.id}`, { is_featured: !item.is_featured });
      setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, ...data } : it)));
      toast.success(data.is_featured ? "Featured." : "Unfeatured.");
    } catch {
      toast.error("Couldn't update.");
    }
  };

  const toggleActive = async (item) => {
    const willActivate = item.is_active === false;
    try {
      await api.post(`/admin/curated/${item.id}/${willActivate ? "activate" : "deactivate"}`);
      setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, is_active: willActivate } : it)));
      toast.success(willActivate ? "Now visible on Browse." : "Hidden from Browse.");
    } catch {
      toast.error("Couldn't update visibility.");
    }
  };

  const purgeInactive = async () => {
    const inactiveCount = items.filter((it) => it.is_active === false).length;
    if (inactiveCount === 0) {
      toast.message("No hidden items to purge.");
      return;
    }
    if (!window.confirm(`Permanently delete all ${inactiveCount} hidden items? This cannot be undone.`)) return;
    try {
      const { data } = await api.delete("/admin/curated/inactive/purge");
      toast.success(`Purged ${data.deleted} hidden products.`);
      setItems((prev) => prev.filter((it) => it.is_active !== false));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't purge.");
    }
  };

  const filtered = useMemo(() => {
    let list = items;
    if (showHidden) {
      list = list.filter((it) => it.is_active === false);
    } else {
      list = list.filter((it) => it.is_active !== false);
    }
    if (filterCategory !== "All") list = list.filter((it) => it.category === filterCategory);
    if (onlyFeatured) list = list.filter((it) => it.is_featured);
    return list;
  }, [items, filterCategory, onlyFeatured, showHidden]);

  const counts = useMemo(() => {
    const visible = items.filter((it) => it.is_active !== false);
    const c = { All: visible.length };
    for (const cat of CATEGORIES) c[cat] = visible.filter((it) => it.category === cat).length;
    return c;
  }, [items]);

  const featuredCount = items.filter((it) => it.is_featured && it.is_active !== false).length;
  const hiddenCount = items.filter((it) => it.is_active === false).length;

  const handleCSV = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const rows = parseCSV(text);
      if (rows.length < 2) {
        toast.error("CSV is empty or missing a header row.");
        return;
      }
      const header = rows[0].map((h) => h.trim().toLowerCase());
      const required = ["product_name", "base_url", "category"];
      for (const req of required) {
        if (!header.includes(req)) {
          toast.error(`CSV missing required column: ${req}`);
          return;
        }
      }
      const idx = Object.fromEntries(header.map((h, i) => [h, i]));
      const payload = rows.slice(1).map((r) => ({
        product_name: (r[idx.product_name] || "").trim(),
        base_url: (r[idx.base_url] || "").trim(),
        image_url: idx.image_url !== undefined ? (r[idx.image_url] || "").trim() : "",
        category: (r[idx.category] || "").trim(),
        description: idx.description !== undefined ? (r[idx.description] || "").trim() : "",
        price: idx.price !== undefined ? parseFloat(r[idx.price] || "0") || 0 : 0,
        tags: idx.tags !== undefined ? parseTags(r[idx.tags]) : [],
        is_featured: idx.is_featured !== undefined ? parseBool(r[idx.is_featured]) : false,
      }));
      const { data } = await api.post("/admin/curated/bulk", payload);
      if (data.imported > 0) {
        toast.success(`Imported ${data.imported} product${data.imported === 1 ? "" : "s"}${data.failed ? ` · ${data.failed} skipped` : ""}.`);
      } else {
        toast.error(`No products imported. ${data.failed} row${data.failed === 1 ? "" : "s"} failed validation.`);
      }
      if (data.errors?.length) {
        console.warn("CSV import errors:", data.errors);
      }
      await load();
    } catch (err) {
      toast.error("Couldn't parse or import CSV. Check the file format.");
      console.error(err);
    } finally { setImporting(false); }
  };

  if (editing) {
    return <ProductEditor product={editing === "new" ? null : editing} onBack={() => { setEditing(null); load(); }} />;
  }

  return (
    <div data-testid="admin-products-manager">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="font-manrope text-sm text-ink-soft">
          {items.length} curated {items.length === 1 ? "product" : "products"} total · {featuredCount} featured
        </p>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={downloadTemplate}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium bg-white text-ink-soft border border-[#EBE2E8] hover:bg-brand-soft/40"
            data-testid="admin-products-template-btn"
          >
            <Download className="h-4 w-4" /> CSV template
          </button>
          <label
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium bg-white text-ink-soft border border-[#EBE2E8] cursor-pointer hover:bg-brand-soft/40 ${importing ? "opacity-60 pointer-events-none" : ""}`}
            data-testid="admin-products-import-btn"
          >
            <Upload className="h-4 w-4" /> {importing ? "Importing…" : "Import CSV"}
            <input type="file" accept=".csv,text/csv" onChange={handleCSV} className="hidden" data-testid="admin-products-csv-input" />
          </label>
          <button onClick={() => setEditing("new")} className="btn-primary inline-flex items-center gap-2" data-testid="admin-products-new-btn">
            <Plus className="h-4 w-4" /> New product
          </button>
        </div>
      </div>

      {/* Category filter chips */}
      <div className="mt-6 flex gap-2 flex-wrap">
        {(["All", ...CATEGORIES]).map((cat) => (
          <button
            key={cat}
            onClick={() => setFilterCategory(cat)}
            className={`rounded-full px-3.5 py-1.5 text-xs font-medium border ${filterCategory === cat ? "bg-brand text-white border-brand" : "bg-white text-ink-soft border-[#EBE2E8] hover:bg-brand-soft/40"}`}
            data-testid={`admin-products-filter-${cat.replace(/\s+/g, "-").toLowerCase()}`}
          >
            {cat} <span className="opacity-60">· {counts[cat] || 0}</span>
          </button>
        ))}
        <button
          onClick={() => setOnlyFeatured((v) => !v)}
          className={`rounded-full px-3.5 py-1.5 text-xs font-medium border inline-flex items-center gap-1.5 ${onlyFeatured ? "bg-amber-400 text-white border-amber-400" : "bg-white text-ink-soft border-[#EBE2E8] hover:bg-amber-50"}`}
          data-testid="admin-products-filter-featured"
        >
          <Star className={`h-3 w-3 ${onlyFeatured ? "fill-white" : ""}`} /> Featured only · {featuredCount}
        </button>
        <button
          onClick={() => { setShowHidden((v) => !v); setFilterCategory("All"); setOnlyFeatured(false); }}
          className={`rounded-full px-3.5 py-1.5 text-xs font-medium border inline-flex items-center gap-1.5 ${showHidden ? "bg-ink text-white border-ink" : "bg-white text-ink-soft border-[#EBE2E8] hover:bg-gray-100"}`}
          data-testid="admin-products-filter-hidden"
        >
          <EyeOff className="h-3 w-3" /> Hidden · {hiddenCount}
        </button>
        {showHidden && hiddenCount > 0 && (
          <button
            onClick={purgeInactive}
            className="rounded-full px-3.5 py-1.5 text-xs font-medium border bg-red-50 text-red-700 border-red-200 hover:bg-red-100 inline-flex items-center gap-1.5"
            data-testid="admin-products-purge-hidden"
          >
            <Trash2 className="h-3 w-3" /> Purge all hidden
          </button>
        )}
      </div>

      {loading ? (
        <p className="mt-8 font-manrope text-ink-soft animate-soft-pulse">Loading products…</p>
      ) : filtered.length === 0 ? (
        <div className="mt-8 card-soft p-10 text-center" data-testid="admin-products-empty">
          <Package className="h-8 w-8 text-brand mx-auto mb-3 opacity-40" />
          <p className="font-outfit text-lg text-ink">
            {items.length === 0 ? "No curated products yet." : "No products match this filter."}
          </p>
          <p className="font-manrope text-sm text-ink-soft mt-2">
            Add products manually or bulk-import via CSV.
          </p>
        </div>
      ) : (
        <div className="mt-6 grid gap-3">
          {filtered.map((it) => (
            <div key={it.id} className="card-soft p-4 flex items-center gap-4" data-testid={`admin-product-${it.id}`}>
              <div className="h-14 w-14 rounded-xl bg-brand-soft/60 overflow-hidden shrink-0 flex items-center justify-center">
                {it.image_url ? (
                  <img src={it.image_url} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ) : (
                  <Package className="h-5 w-5 text-brand opacity-60" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {it.is_active === false && (
                    <span className="tag-pill text-xs !bg-gray-200 !text-gray-700 inline-flex items-center gap-1" data-testid={`admin-product-hidden-${it.id}`}>
                      <EyeOff className="h-3 w-3" /> Hidden
                    </span>
                  )}
                  {it.is_featured && (
                    <span className="tag-pill text-xs !bg-amber-100 !text-amber-800 inline-flex items-center gap-1" data-testid={`admin-product-featured-${it.id}`}>
                      <Star className="h-3 w-3 fill-amber-600 text-amber-600" /> Featured
                    </span>
                  )}
                  <span className="tag-pill text-xs">{it.category}</span>
                  {it.price ? <span className="font-manrope text-xs text-ink-soft">${Number(it.price).toFixed(2)}</span> : null}
                  {(it.tags || []).slice(0, 4).map((t) => (
                    <span key={t} className="tag-pill text-xs !bg-brand-soft/50 !text-ink-soft">#{t}</span>
                  ))}
                </div>
                <h3 className="mt-1 font-outfit text-base font-medium text-ink truncate">{it.product_name}</h3>
                <a href={it.base_url} target="_blank" rel="noopener noreferrer" className="mt-0.5 font-manrope text-xs text-ink-soft truncate block hover:text-brand">
                  {it.base_url}
                </a>
              </div>
              <div className="flex gap-1 shrink-0">
                <button
                  onClick={() => toggleActive(it)}
                  className={`p-2 rounded-full hover:bg-brand-soft ${it.is_active === false ? "text-ink-soft" : "text-brand"}`}
                  title={it.is_active === false ? "Show on Browse page" : "Hide from Browse page"}
                  data-testid={`admin-product-active-toggle-${it.id}`}
                >
                  {it.is_active === false ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
                <button
                  onClick={() => toggleFeatured(it)}
                  className={`p-2 rounded-full hover:bg-amber-50 ${it.is_featured ? "text-amber-600" : "text-ink-soft"}`}
                  title={it.is_featured ? "Unfeature" : "Feature this product"}
                  data-testid={`admin-product-feature-toggle-${it.id}`}
                >
                  <Star className={`h-4 w-4 ${it.is_featured ? "fill-amber-500" : ""}`} />
                </button>
                <a href={it.base_url} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full hover:bg-brand-soft" title="Visit" data-testid={`admin-product-visit-${it.id}`}>
                  <ExternalLink className="h-4 w-4 text-ink-soft" />
                </a>
                <button onClick={() => setEditing(it)} className="p-2 rounded-full hover:bg-brand-soft" title="Edit" data-testid={`admin-product-edit-${it.id}`}>
                  <Pencil className="h-4 w-4 text-ink-soft" />
                </button>
                <button onClick={() => remove(it.id)} className="p-2 rounded-full hover:bg-red-50" title="Delete" data-testid={`admin-product-delete-${it.id}`}>
                  <Trash2 className="h-4 w-4 text-red-600" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductEditor({ product, onBack }) {
  const isNew = !product;
  const [form, setForm] = useState({
    product_name: product?.product_name || "",
    base_url: product?.base_url || "",
    image_url: product?.image_url || "",
    category: product?.category || "Recovery",
    description: product?.description || "",
    price: product?.price || 0,
    tags: product?.tags || [],
    is_featured: !!product?.is_featured,
  });
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);

  let saveLabel = isNew ? "Add product" : "Save changes";
  if (saving) saveLabel = "Saving…";

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addTag = (raw) => {
    const t = (raw || "").trim().toLowerCase();
    if (!t) return;
    if (form.tags.includes(t)) { setTagInput(""); return; }
    setForm((f) => ({ ...f, tags: [...f.tags, t] }));
    setTagInput("");
  };

  const removeTag = (t) => setForm((f) => ({ ...f, tags: f.tags.filter((x) => x !== t) }));

  const onTagKey = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
      if (tagInput.trim()) { e.preventDefault(); addTag(tagInput); }
    } else if (e.key === "Backspace" && !tagInput && form.tags.length) {
      removeTag(form.tags[form.tags.length - 1]);
    }
  };

  const save = async () => {
    if (!form.product_name.trim() || !form.base_url.trim()) {
      toast.error("Product name and URL are required.");
      return;
    }
    if (!CATEGORIES.includes(form.category)) {
      toast.error("Please select a valid category.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        product_name: form.product_name.trim(),
        base_url: form.base_url.trim(),
        image_url: form.image_url.trim(),
        description: form.description.trim(),
        price: parseFloat(form.price) || 0,
        tags: form.tags,
        is_featured: !!form.is_featured,
      };
      if (isNew) {
        await api.post("/admin/curated", payload);
        toast.success("Product added.");
      } else {
        await api.put(`/admin/curated/${product.id}`, payload);
        toast.success("Product updated.");
      }
      onBack();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save product.");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-product-editor">
      <button onClick={onBack} className="font-manrope text-sm text-ink-soft hover:text-brand" data-testid="admin-product-back-btn">
        ← Back to products
      </button>

      <h2 className="mt-4 font-outfit text-2xl font-medium text-ink">
        {isNew ? "New product" : "Edit product"}
      </h2>

      <div className="mt-6 grid gap-5 max-w-2xl">
        <Field label="Product name *">
          <input
            type="text"
            value={form.product_name}
            onChange={(e) => set("product_name", e.target.value)}
            className="input-soft"
            placeholder="Memory Foam Wedge Pillow"
            data-testid="admin-product-name-input"
          />
        </Field>

        <Field label="Product URL *">
          <input
            type="url"
            value={form.base_url}
            onChange={(e) => set("base_url", e.target.value)}
            className="input-soft"
            placeholder="https://www.amazon.com/dp/..."
            data-testid="admin-product-url-input"
          />
        </Field>

        <Field label="Image URL">
          <input
            type="url"
            value={form.image_url}
            onChange={(e) => set("image_url", e.target.value)}
            className="input-soft"
            placeholder="https://..."
            data-testid="admin-product-image-input"
          />
          {form.image_url ? (
            <img src={form.image_url} alt="preview" className="mt-3 h-24 w-24 rounded-xl object-cover border border-[#EBE2E8]" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : null}
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Category *">
            <select
              value={form.category}
              onChange={(e) => set("category", e.target.value)}
              className="input-soft"
              data-testid="admin-product-category-select"
            >
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Price (USD)">
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.price}
              onChange={(e) => set("price", e.target.value)}
              className="input-soft"
              data-testid="admin-product-price-input"
            />
          </Field>
        </div>

        <Field label="Tags (press Enter or comma to add)">
          <div className="flex flex-wrap items-center gap-2 p-2 rounded-xl border border-[#EBE2E8] bg-white focus-within:ring-2 focus-within:ring-brand/30">
            {form.tags.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 rounded-full bg-brand-soft text-brand px-2.5 py-1 text-xs font-medium" data-testid={`admin-product-tag-${t}`}>
                #{t}
                <button type="button" onClick={() => removeTag(t)} className="hover:text-red-600" aria-label={`Remove tag ${t}`}>
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={onTagKey}
              onBlur={() => tagInput.trim() && addTag(tagInput)}
              placeholder={form.tags.length ? "" : "sleep, gift-under-30, post-op…"}
              className="flex-1 min-w-[120px] outline-none bg-transparent font-manrope text-sm text-ink px-1"
              data-testid="admin-product-tag-input"
            />
          </div>
          <p className="mt-1.5 font-manrope text-xs text-ink-soft">
            Tags help filter products. Use lowercase, hyphenated (e.g. <code>gift-under-30</code>).
          </p>
        </Field>

        <Field label="Description">
          <textarea
            rows={3}
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            className="input-soft"
            placeholder="A short, gentle description shown to gifters."
            data-testid="admin-product-description-input"
          />
        </Field>

        <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="admin-product-featured-toggle">
          <input
            type="checkbox"
            checked={form.is_featured}
            onChange={(e) => set("is_featured", e.target.checked)}
            className="h-5 w-5 rounded border-[#EBE2E8] text-amber-500 focus:ring-amber-400"
          />
          <span className="inline-flex items-center gap-2">
            <Star className={`h-4 w-4 ${form.is_featured ? "fill-amber-500 text-amber-500" : "text-ink-soft"}`} />
            <span className="font-manrope text-sm text-ink">Feature this product</span>
            <span className="font-manrope text-xs text-ink-soft">— surfaces on the Browse page</span>
          </span>
        </label>

        <div className="flex gap-3">
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="admin-product-save-btn">
            {saveLabel}
          </button>
          <button onClick={onBack} className="rounded-full px-5 py-2.5 text-sm font-medium bg-white text-ink-soft border border-[#EBE2E8]" data-testid="admin-product-cancel-btn">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-manrope text-xs text-ink-soft uppercase tracking-wider">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
